// DeckResourceStartPanel.jsx
//
// In-column “start here” surface for model/agent deck columns: load metadata,
// show a short overview (not a copy of /m or /g pages), then create the
// conversation only after the user sends a real first message.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Pressable,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import {
  useAuthStore,
  useChatStore,
  useModelStore,
  useAgentStore,
  useCompanyStore,
  useToastStore,
  useModalStore,
  useChatInputStore,
} from '@/store';
import { agentsApi } from '@/lib/api';
import socketManager from '@/lib/socket';
import { isModelLockedForUser, openModelAccessPaywall } from '@/constants/utils/trial';
import { CHAT_INPUT_STYLE } from '@/constants/config';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ChatInput from '@/components/ChatInput/ChatInput';

export default function DeckResourceStartPanel({
  kind,
  columnId,
  deckSlug,
  resourceId,
  onConversationStarted,
  onPickDifferent,
  recipeHint, // { label, subtitle, modality, stepIndex, totalSteps }
}) {
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSending, setIsSending] = useState(false);

  const user = useAuthStore((s) => s.user);
  const userId = user?.id;
  const isPlus = user?.flags?.isPlus;
  const isGhost = user?.flags?.isGhost;
  const chatCount = useChatStore((s) => s.chats.length);
  const createConversation = useChatStore((s) => s.createConversation);
  const { addToast } = useToastStore((s) => s.actions);
  const { openModal } = useModalStore((s) => s.actions);
  const setInputText = useChatInputStore((s) => s.setText);
  const setInputContext = useChatInputStore((s) => s.setContext);
  const clearInput = useChatInputStore((s) => s.clearInput);

  const model = useModelStore((s) => (kind === 'model' ? s.getModelById(resourceId) : null));
  const fetchModelById = useModelStore((s) => s.fetchModelById);
  const agent = useAgentStore((s) => (kind === 'agent' ? s.getAgentById(resourceId) : null));
  const addAgents = useAgentStore((s) => s.addAgents);
  const getCompanyById = useCompanyStore((s) => s.getCompanyById);

  const company = useMemo(() => {
    if (kind !== 'model' || !model?.companyId) return null;
    return getCompanyById(model.companyId);
  }, [kind, model?.companyId, getCompanyById]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        if (kind === 'model') {
          await fetchModelById(resourceId);
        } else {
          const agentData = await agentsApi.getAgentById(resourceId);
          if (agentData) addAgents([agentData]);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'Could not load this resource.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, resourceId, fetchModelById, addAgents]);

  const resource = kind === 'model' ? model : agent;
  const title = resource?.name || (kind === 'model' ? 'Model' : 'Agent');
  const subtitle =
    kind === 'model'
      ? (model?.subtitle || model?.description || '')
      : (agent?.description || '');
  const starters = useMemo(() => {
    if (kind === 'model' && Array.isArray(model?.conversationStarters)) {
      return model.conversationStarters.filter((s) => typeof s === 'string' && s.trim()).slice(0, 6);
    }
    if (kind === 'agent' && Array.isArray(agent?.defaultPrompts)) {
      return agent.defaultPrompts
        .map((p) => (typeof p === 'string' ? p : p?.text || p?.prompt || ''))
        .filter(Boolean)
        .slice(0, 6);
    }
    return [];
  }, [kind, model?.conversationStarters, agent?.defaultPrompts]);

  const plusLocked =
    kind === 'model'
      ? isModelLockedForUser(user, model)
      : agent?.flags?.isPlusOnly && !isPlus;

  // Per-resource composer context so the ChatInput state (text, files,
  // parameters) is scoped to this specific deck column and doesn't bleed
  // into the global "new conversation" buffer.
  const composerContextId = useMemo(() => {
    if (!resourceId) return null;
    return `deck-col-${deckSlug}-${columnId}-${kind}-${resourceId}`;
  }, [deckSlug, columnId, kind, resourceId]);

  const memoizedModelIds = useMemo(
    () => (kind === 'model' && resourceId ? [resourceId] : []),
    [kind, resourceId],
  );
  const memoizedAgentIds = useMemo(
    () => (kind === 'agent' && resourceId ? [resourceId] : []),
    [kind, resourceId],
  );

  // Bind the input store to our scoped context so credit calc + selection
  // operate against the right slot.
  useEffect(() => {
    if (!composerContextId) return undefined;
    setInputContext(composerContextId);
    return () => {};
  }, [composerContextId, setInputContext]);

  const handleSetStarter = useCallback(
    (s) => {
      if (!composerContextId) return;
      setInputContext(composerContextId);
      setInputText(s, composerContextId);
    },
    [composerContextId, setInputContext, setInputText],
  );

  // Receives the formatted-message payload from <ChatInput>'s send button.
  const handleSend = useCallback(
    async (formattedMessage) => {
      const text = String(formattedMessage?.text || '').trim();
      // ChatInput validates required text/files/parameters before invoking
      // onSend. Media models can legitimately submit an empty text body.
      if (!formattedMessage || isSending) return false;

      if (isGhost) {
        if (chatCount >= 1) {
          openModal('GhostSignupModal');
          return false;
        }
        addToast('Sign in with a full account to start a chat.', 'error');
        return false;
      }

      if (!(await socketManager.requireConnection())) {
        return false;
      }

      if (plusLocked) {
        if (kind === 'model') {
          openModelAccessPaywall(openModal, {
            model,
            title: 'Access Premium AI Models',
            subtitle: 'To chat with this model, buy credits or upgrade to Plus.',
          });
        } else {
          openModal('PlusFeatureModal', { feature: 'bots' });
        }
        return false;
      }

      // The server checks the live balance; cached credits must not block a Chat send.

      setIsSending(true);
      try {
        const payload = {
          ...formattedMessage,
          text,
          // Pin the resource so the ChatInput's selected-model overrides
          // can't drop us out of the column's model/agent.
          modelIds: kind === 'model' ? [resourceId] : (formattedMessage?.modelIds || []),
          agentIds: kind === 'agent' ? [resourceId] : (formattedMessage?.agentIds || []),
        };

        const conversation = await createConversation({ userId, formattedMessage: payload });
        if (!conversation?.id) throw new Error('Failed to create conversation');

        setTimeout(() => {
          socketManager.emitKickConversation(userId, conversation.id);
        }, 250);

        // Hand the now-live conversation id back to the parent column and
        // wipe the scoped composer so the column promotes to a chat column
        // with a clean slate.
        if (composerContextId) {
          clearInput(composerContextId);
        }
        onConversationStarted(conversation.id);
        return true;
      } catch (err) {
        addToast(err?.message || 'Failed to start chat', 'error');
        // Keep the composer text so the user can retry without retyping.
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [
      isSending,
      isGhost,
      chatCount,
      plusLocked,
      model,
      kind,
      resourceId,
      userId,
      createConversation,
      onConversationStarted,
      addToast,
      openModal,
      composerContextId,
      clearInput,
    ],
  );

  if (loading) {
    return (
      <View style={styles.centerStack}>
        <ActivityIndicator color={colors.text_color_secondary} />
        <Text size="xs" color="secondary" style={{ marginTop: 10 }}>
          Loading…
        </Text>
      </View>
    );
  }

  if (loadError || !resource) {
    return (
      <View style={styles.centerStack}>
        <Text size="xs" color="secondary" style={{ textAlign: 'center', paddingHorizontal: 12 }}>
          {loadError || 'This resource is unavailable.'}
        </Text>
        <Pressable
          onPress={onPickDifferent}
          style={({ hovered }) => [
            styles.secondaryBtn,
            { borderColor: colors.border_color_secondary },
            Platform.OS === 'web' && hovered ? { backgroundColor: colors.solid_color_tertiary } : null,
          ]}
        >
          <Text size="xs" weight="bold" color="secondary">
            {kind === 'model' ? 'Choose another model' : 'Choose another agent'}
          </Text>
        </Pressable>
      </View>
    );
  }

  if (isGhost) {
    return (
      <View style={styles.centerStack}>
        <Text size="xs" color="secondary" style={{ textAlign: 'center', paddingHorizontal: 14, lineHeight: 18 }}>
          Guest mode cannot create new chats. Sign in to send your first message with {title}.
        </Text>
        <Pressable
          onPress={onPickDifferent}
          style={({ hovered }) => [
            styles.secondaryBtn,
            { borderColor: colors.border_color_secondary },
            Platform.OS === 'web' && hovered ? { backgroundColor: colors.solid_color_tertiary } : null,
          ]}
        >
          <Text size="xs" weight="bold" color="secondary">
            Back to list
          </Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.heroCard, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
          <View style={styles.heroTop}>
            <View
              style={[
                styles.heroAvatar,
                {
                  backgroundColor:
                    kind === 'model'
                      ? model?.logoPath
                        ? '#fff'
                        : colors.solid_color_tertiary
                      : colors.solid_color_tertiary,
                },
              ]}
            >
              {kind === 'model' && model?.logoPath ? (
                <Image source={{ uri: model.logoPath }} style={styles.heroAvatarImg} contentFit="cover" />
              ) : kind === 'agent' && agent?.avatarPath ? (
                <Image source={{ uri: agent.avatarPath }} style={styles.heroAvatarImg} contentFit="cover" />
              ) : (
                <Icon
                  name={kind === 'model' ? 'cube' : 'user-astronaut'}
                  size={22}
                  color={colors.text_color_secondary}
                />
              )}
            </View>
            <View style={styles.heroTextCol}>
              {kind === 'model' && company?.name ? (
                <Text size="xxs" color="tertiary" numberOfLines={1} weight="semibold">
                  {company.name}
                </Text>
              ) : null}
              <Text size="l" weight="semibold" color="primary" numberOfLines={2}>
                {title}
              </Text>
              {plusLocked ? (
                <View style={styles.plusHint}>
                  <Icon name="sparkles" size={12} color={colors.text_color_secondary} />
                  <Text size="xxs" color="secondary" weight="semibold">
                    Plus only — upgrade to chat
                  </Text>
                </View>
              ) : null}
            </View>
          </View>
          {!!subtitle && (
            <Text size="s" color="secondary" style={styles.desc}>
              {subtitle}
            </Text>
          )}
          {kind === 'agent' && Array.isArray(agent?.outputTypes) && agent.outputTypes.length > 0 ? (
            <Text size="xxs" color="tertiary" style={styles.metaLine}>
              Outputs: {agent.outputTypes.join(' · ')}
            </Text>
          ) : null}

          {/* Recipe-hint banner — shown when the column was cloned from a
              DeckTemplate. Reminds the user what this specific column is
              meant to produce so the jump from the recipe page → empty deck
              feels continuous instead of vertiginous. */}
          {recipeHint && (recipeHint.label || recipeHint.subtitle) ? (
            <View style={[styles.recipeHint, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
              <View style={styles.recipeHintHeader}>
                <Icon name="table-columns" size={10} color={colors.text_color_tertiary} solid />
                <Text size="xxs" weight="bold" color="tertiary" style={styles.recipeHintEyebrow}>
                  {`From recipe${recipeHint.stepIndex != null && recipeHint.totalSteps != null
                    ? ` · Step ${recipeHint.stepIndex + 1} of ${recipeHint.totalSteps}`
                    : ''}`}
                </Text>
              </View>
              {!!recipeHint.label && (
                <Text size="xs" weight="bold" color="primary" style={styles.recipeHintTitle}>
                  {recipeHint.label}
                </Text>
              )}
              {!!recipeHint.subtitle && (
                <Text size="xxs" color="secondary" style={styles.recipeHintCopy}>
                  {recipeHint.subtitle}
                </Text>
              )}
            </View>
          ) : null}
        </View>

        {starters.length > 0 ? (
          <View style={styles.startersBlock}>
            <Text size="xs" color="tertiary" style={styles.startersLabel}>
              Try asking
            </Text>
            <View style={styles.startersRow}>
              {starters.map((s, i) => (
                <Pressable
                  key={`${i}-${s.slice(0, 24)}`}
                  onPress={() => handleSetStarter(s)}
                  style={({ hovered }) => [
                    styles.starterChip,
                    { backgroundColor: colors.solid_color_secondary },
                    Platform.OS === 'web' && hovered ? { backgroundColor: colors.solid_color_tertiary } : null,
                  ]}
                >
                  <Text size="s" color="secondary" numberOfLines={6} style={{ lineHeight: 22 }}>
                    {s}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>
        ) : null}

        <Text size="xxs" color="tertiary" style={styles.hint}>
          Send a message to start this conversation.
        </Text>
      </ScrollView>

      {/* Real composer — same ChatInputFixed the rest of the app uses, so
          the user gets credits / model selector / file uploads / dictate /
          inline params parity with chat pages instead of the lobotomised
          TextInput this panel used to ship. */}
      <View style={styles.composerSlot}>
        <ChatInput
          key={composerContextId || `deck-col-${kind}-${resourceId}`}
          conversationId={composerContextId || `deck-col-${kind}-${resourceId}`}
          forceInputStyle={CHAT_INPUT_STYLE.FIXED}
          modelIds={memoizedModelIds}
          agentIds={memoizedAgentIds}
          onSend={handleSend}
          placeholder={plusLocked ? 'Upgrade to send a message…' : 'Send your first message…'}
          isDisabled={isSending || plusLocked}
          isLoading={isSending}
          withModels={false}
          showGhostBanner={isGhost}
          hideVoiceMode
          isCompact
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: 0,
  },
  scroll: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    padding: 24,
    gap: 24,
  },
  centerStack: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    gap: 12,
  },
  heroCard: {
    gap: 16,
  },
  heroTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  heroAvatar: {
    width: 52,
    height: 52,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  heroAvatarImg: {
    width: 52,
    height: 52,
  },
  heroTextCol: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  plusHint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  desc: {
    lineHeight: 22,
  },
  metaLine: {
    marginTop: 2,
  },
  startersBlock: {
    gap: 6,
  },
  startersLabel: {
    letterSpacing: 0.3,
    marginTop: 5,
    marginBottom: 10,
  },
  startersRow: {
    gap: 12,
  },
  starterChip: {
    padding: 16,
    borderRadius: 12,
  },
  hint: {
    lineHeight: 16,
    paddingHorizontal: 2,
  },
  recipeHint: {
    paddingTop: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: 8,
  },
  recipeHintHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  recipeHintEyebrow: {
    fontSize: 12,
  },
  recipeHintTitle: { marginTop: 2 },
  recipeHintCopy: { lineHeight: 15 },
  composerSlot: {
    width: '100%',
    flexShrink: 0,
  },
  secondaryBtn: {
    marginTop: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
});
